import React, { Component } from 'react';
import queryString from 'query-string';
import { withRouter, Route } from 'react-router-dom';
import { withCookies } from "react-cookie";

import Account from './components/account/Account';
import AddProject from "./components/projects/AddProject";
import Analysis from "./components/analysis/Analysis";
import AnalysisModuleLibrary from "./components/analysis/AnalysisModuleLibrary";
import AppContainer from './components/shared/AppContainer';
import { AuthContext } from './authContext';
import { authCookieName, citycadMajorVersion, cookieExpirySeconds } from "./configuration";
import {
  authenticate,
  authenticateToken,
  getAllHelpContent,
  getLatestCityCADVersion,
  getUserDetails
} from "./components/data/data";
import Header from './components/Header';
import Help from './components/help/Help';
import HelpArticle from './components/help/HelpArticle';
import HelpCategory from './components/help/HelpCategory';
import Homepage from './components/homepage/Homepage';
import Library from "./components/library/Library";
import LibraryCategory from "./components/library/LibraryCategory";
import listenForTitleChanges from './components/shared/utilities/listenForTitleChanges';
import LoginForm from "./components/login/LoginForm";
import ModelLocation from "./components/modellocation/ModelLocation";
import Notification from "./components/shared/Notification";
import NotificationContainer from "./components/shared/NotificationContainer";
import processHelpData from "./components/help/processHelpData";
import Projects from './components/projects/Projects';
import Project from './components/projects/Project';
import { updateResourceFileSize } from "./components/data/dataCIMToolkit";

import Version from "./components/shared/utilities/version";
import PageHeading from "./components/shared/PageHeading";
import {isActiveSiteLayoutToolkitUser} from "./components/shared/utilities/isActiveSiteLayoutToolkitUser";
import Image from "./components/shared/Image";
import siteLayoutToolkitImage from "./assets/site-layout-toolkit-logo.png";
import SiteLayoutToolkitHeader from "./components/sitelayouttoolkit/SiteLayoutToolkitHeader";

const defaultUserDetails = {
  firstName: "",
  userID: undefined,
  contactID: undefined,
  token: "",
  email: "",
  licences: []
};

class App extends Component {
  state = {
    isCityCAD: false,
    citycadVersion: {
      majorVersion: null,
      minorVersion: null,
      build: null,
      month: null,
      day: null,
      versionString: "",
      isLatest: null
    },
    helpData: null,
    isErrorAuthenticating: false,
    isTokenExpired: false,
    isLoggedIn: false,
    isLoggingIn: false,
    isNewFileSizeReady: false,
    isShowLoginForm: false,
    userDetails: defaultUserDetails,
    messages: [],
    notifications: [],
    isLoading: true
  };
  componentDidMount = async () => {
    await this.checkCityCAD();
    await this.checkToken();
    await this.loadHelpArticles();
    await this.setState({isLoading: false});
    listenForTitleChanges(this.addNotification, this.updateResourceFileSize);
  };
  addNotification = async notification => {
    let newNotifications = this.state.notifications;
    newNotifications.push(notification);
    await this.setState({notifications: newNotifications});
  };
  cancelNewFileSizeReady = async () => {
    await this.setState({isNewFileSizeReady: false});
  };
  checkCityCAD = async () => {

    let latestVersionData = await getLatestCityCADVersion();
    let latestVersionString = latestVersionData.data.latestVersion; // "3.2.1.0726";

    /*
     * Attempt to find citycad and version in URL query strings
     */
    const parsedURL = queryString.parse(window.location.search);
    let isCityCAD = parsedURL.citycad;
    let versionString = parsedURL.version;

    /*
     * If no citycad or version included in string, then try to get from cookie
     */
    if (isCityCAD === undefined && this.props.cookies.get('isCityCAD') !== undefined) {
      isCityCAD = this.props.cookies.get('isCityCAD');
    }

    if (versionString === undefined && this.props.cookies.get('citycadVersion') !== undefined) {
      versionString = this.props.cookies.get('citycadVersion');
    }

    /*
     * Update citycad and version in state and save new cookies
     */
    if (isCityCAD === "true") {
      await this.setState({isCityCAD: true});
      this.props.cookies.set('isCityCAD', true, {path: "/", maxAge: cookieExpirySeconds});
    }
    else {
      this.props.cookies.set('isCityCAD', false, {path: "/", maxAge: cookieExpirySeconds});
    }

    if (versionString !== undefined) {
      const version = Version(versionString, latestVersionString);
      await this.setState({citycadVersion: version});
      this.props.cookies.set('citycadVersion', versionString, {path: "/", maxAge: cookieExpirySeconds});
    }
    else {
      this.props.cookies.set('citycadVersion', "", {path: "/", maxAge: cookieExpirySeconds});
    }

  };
  checkToken = async () => {
    if (this.props.cookies.get(authCookieName) !== undefined) {
      const response = await authenticateToken(this.props.cookies.get(authCookieName));
      this.handleAuthenticationResponse(response, true);
    }
  };
  handleAuthenticationResponse = async (response, isToken) => {
    console.log("App.js > handleAuthenticationResponse > response", response)
    if (response.data.response === 200) {

      const userDetails = await getUserDetails(response.data.token);

      await this.setState({
        isLoggedIn: true,
        isLoggingIn: false,
        isErrorAuthenticating: false,
        isTokenExpired: false,
        isShowLoginForm: false,
        messages: response.data.messages,
        userDetails: {
          firstName: response.data.name,
          userID: Number(response.data.userID),
          contactID: Number(response.data.contactID),
          token: response.data.token,
          email: userDetails.data.email,
          licences: userDetails.data.licences
        }
      });
      this.saveAuthenticationCookie(response.data.token);
      if (this.props.location.pathname === "/login")
        this.props.history.push("/");
    }
    else {
      await this.setState({
        isLoggingIn: false,
        isErrorAuthenticating: !isToken,
        isTokenExpired: isToken
      });
      if (isToken)
        this.removeAuthenticationCookie();
    }
  };
  handleLogin = async (username, password) => {
    await this.setState({isLoggingIn: true});
    const response = await authenticate(username, password);
    this.handleAuthenticationResponse(response, false);
  };
  handleLogout = async () => {
    await this.setState({isShowLoginForm: false, isLoggedIn: false, userDetails: defaultUserDetails, messages: []});
    this.removeAuthenticationCookie();
    this.props.history.push("/");
  };
  loadHelpArticles = async () => {
    const helpArticles = await getAllHelpContent(citycadMajorVersion);
    const helpData = await processHelpData(helpArticles);
    await this.setState({ helpData: helpData });
  };
  navigate = path => {
    this.props.history.push(path);
  };
  removeAuthenticationCookie = () => {
    this.props.cookies.remove(authCookieName);
  };
  removeNotification = async (id) => {
    let notifications = this.state.notifications;
    let newNotifications = notifications.reduce(
      (result, notification) => {
        if (notification.id !== id)
          result.push(notification);
        return result;
      }, []
    );
    await this.setState({notifications: newNotifications});
  };
  saveAuthenticationCookie = token => {
    this.props.cookies.set(authCookieName, token, {path: "/", maxAge: cookieExpirySeconds});
  };
  toggleLoginForm = async () => { await this.setState({ isShowLoginForm: !this.state.isShowLoginForm }); };
  updateResourceFileSize = async resourceID => {
    await updateResourceFileSize(resourceID, this.state.userDetails.token);
    await this.setState({isNewFileSizeReady: true});
  };
  render() {
    return (
      <AuthContext.Provider value={{
        addNotification: this.addNotification,
        authenticate: this.handleLogin,
        cancelNewFileSizeReady: this.cancelNewFileSizeReady,
        citycadVersion: this.state.citycadVersion,
        isCityCAD: this.state.isCityCAD,
        isErrorAuthenticating: this.state.isErrorAuthenticating,
        isTokenExpired: this.state.isTokenExpired,
        isLoggedIn: this.state.isLoggedIn,
        isLoggingIn: this.state.isLoggingIn,
        isNewFileSizeReady: this.state.isNewFileSizeReady,
        isShowLoginForm: this.state.isShowLoginForm,
        logout: this.handleLogout,
        messages: this.state.messages,
        navigate: this.navigate,
        toggleLoginForm: this.toggleLoginForm,
        token: this.state.userDetails.token,
        userDetails: this.state.userDetails
      }}>
        <React.Fragment>
          <Header path={this.props.match.path} currentCategory={this.state.pageCategory} />
          {/*<Route path={'/'} render={props =>*/}
          {/*    <React.Fragment>*/}
          {/*      {*/}
          {/*        isActiveSiteLayoutToolkitUser(this.state.userDetails.licences)*/}
          {/*        &&*/}
          {/*        <SiteLayoutToolkitHeader />*/}
          {/*      }*/}
          {/*    </React.Fragment>*/}
          {/*  }*/}
          {/*  exact*/}
          {/*/>*/}
          <AppContainer>
            {
              this.state.notifications.length > 0
              &&
              <NotificationContainer>
                {
                  this.state.notifications.map(notification => {
                    return <Notification key={notification.id} notificationData={notification} removeNotification={this.removeNotification} />;
                  })
                }
              </NotificationContainer>
            }
            <Route path={`/`} render={props => <Homepage {...props} helpData={this.state.helpData} isLoading={this.state.isLoading} isCityCAD={this.state.isCityCAD} />} exact />
            <Route path={`/login`} component={LoginForm} exact />
            <Route path={`/library`} component={Library} exact />
            <Route path={`/library/category/:id`} component={LibraryCategory} exact />
            <Route path={`/modellocation`} component={ModelLocation} exact />
            <Route path={`/projects`} component={Projects} exact />
            <Route path={`/project/add`} component={AddProject} exact />
            <Route path={`/project/:id`} component={Project} exact />
            <Route path={`/help`} render={props => <Help {...props} helpData={this.state.helpData}/>} exact />
            <Route path={`/help/article/:id`} render={props => <HelpArticle {...props} />} />
            <Route path={`/help/category/:id`} render={props => <HelpCategory {...props} helpData={this.state.helpData} />} />
            <Route path={`/account`} component={Account} />
            <Route path={`/analysis`} component={AnalysisModuleLibrary} exact />
            <Route path={`/analysis/:id`} component={Analysis} exact />
          </AppContainer>
        </React.Fragment>
      </AuthContext.Provider>
    );
  }
}

export default withRouter(withCookies(App));
